/**
 * Telegram WebApp SDK Hook
 * Provides access to Telegram WebApp functionality with fullscreen support
 */

import { useEffect, useState } from 'react';

declare global {
  interface Window {
    Telegram?: {
      WebApp: {
        ready: () => void;
        expand: () => void;
        close: () => void;
        initData: string;
        initDataUnsafe: {
          user?: {
            id: number;
            first_name: string;
            last_name?: string;
            username?: string;
            language_code?: string;
          };
          query_id?: string;
          auth_date?: number;
          hash?: string;
        };
        themeParams: {
          bg_color?: string;
          text_color?: string;
          hint_color?: string;
          link_color?: string;
          button_color?: string;
          button_text_color?: string;
        };
        colorScheme: 'light' | 'dark';
        isExpanded: boolean;
        isFullscreen?: boolean;
        viewportHeight: number;
        viewportStableHeight: number;
        MainButton: {
          text: string;
          color: string;
          textColor: string;
          isVisible: boolean;
          isActive: boolean;
          isProgressVisible: boolean;
          setText: (text: string) => void;
          onClick: (callback: () => void) => void;
          offClick: (callback: () => void) => void;
          show: () => void;
          hide: () => void;
          enable: () => void;
          disable: () => void;
          showProgress: (leaveActive?: boolean) => void;
          hideProgress: () => void;
        };
        BackButton: {
          isVisible: boolean;
          onClick: (callback: () => void) => void;
          offClick: (callback: () => void) => void;
          show: () => void;
          hide: () => void;
        };
        HapticFeedback: {
          impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
          notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
          selectionChanged: () => void;
        };
        openLink: (url: string) => void;
        openTelegramLink: (url: string) => void;
        openInvoice: (url: string, callback?: (status: string) => void) => void;
        showPopup: (params: {
          title?: string;
          message: string;
          buttons?: Array<{ id?: string; type?: string; text: string }>;
        }, callback?: (buttonId: string) => void) => void;
        showAlert: (message: string, callback?: () => void) => void;
        showConfirm: (message: string, callback?: (confirmed: boolean) => void) => void;
        setHeaderColor?: (color: string) => void;
        setBackgroundColor?: (color: string) => void;
        disableVerticalSwipes?: () => void;
        requestFullscreen?: () => void;
        onEvent?: (eventType: string, callback: () => void) => void;
        offEvent?: (eventType: string, callback: () => void) => void;
      };
    };
  }
}

export function useTelegram() {
  const [isReady, setIsReady] = useState(false);
  const [webApp, setWebApp] = useState<any>(null);

  useEffect(() => {
    console.log('📱 Telegram WebApp initialization...');
    if (window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;
      console.log('✅ Telegram WebApp found:', {
        hasInitData: !!tg.initData,
        hasUser: !!tg.initDataUnsafe?.user,
        userId: tg.initDataUnsafe?.user?.id,
        isExpanded: tg.isExpanded,
        isFullscreen: tg.isFullscreen
      });

      // Initialize fullscreen mode
      initializeFullscreen(tg);
      
      tg.ready();
      setWebApp(tg);
      setIsReady(true);
    } else {
      console.warn('⚠️ Telegram WebApp not found - running outside Telegram?');
    }
  }, []);

  const initializeFullscreen = (tg: any) => {
    try {
      // Expand to full height IMMEDIATELY
      tg.expand();
      console.log('📏 WebApp expanded');

      // Force dark theme colors using Telegram API
      if (tg.setHeaderColor) {
        tg.setHeaderColor('#0a0e1a');
        console.log('🎨 Header color set');
      }

      if (tg.setBackgroundColor) {
        tg.setBackgroundColor('#0a0e1a');
        console.log('🎨 Background color set');
      }

      // Disable vertical swipes (prevents minimizing by swiping down)
      if (tg.disableVerticalSwipes) {
        try {
          tg.disableVerticalSwipes();
          console.log('🚫 Vertical swipes disabled');
        } catch (_) {
          console.log('❌ disableVerticalSwipes not supported');
        }
      }

      // Request fullscreen mode (hides Telegram header completely)
      if (tg.requestFullscreen && !tg.isFullscreen) {
        try {
          tg.requestFullscreen();
          console.log('🖥️ Fullscreen requested');
        } catch (e) {
          console.log('❌ Fullscreen not supported in this version');
        }
      }

      // Re-expand if viewport changes (user tries to minimize)
      const viewportChangedHandler = () => {
        console.log('📐 Viewport changed, re-expanding...');
        if (!tg.isExpanded) {
          tg.expand();
        }
        if (tg.requestFullscreen && !tg.isFullscreen) {
          try { tg.requestFullscreen(); } catch (_) { /* not supported */ }
        }
      };

      if (tg.onEvent) {
        tg.onEvent('viewportChanged', viewportChangedHandler);
        console.log('👂 Viewport change listener added');
      }

      // Store handler for cleanup
      (tg as any)._viewportHandler = viewportChangedHandler;

    } catch (error) {
      console.error('❌ Error initializing fullscreen:', error);
    }
  };

  return {
    isReady,
    webApp,
    initData: webApp?.initData || '',
    user: webApp?.initDataUnsafe?.user,
    themeParams: webApp?.themeParams || {},
    colorScheme: webApp?.colorScheme || 'dark',
    mainButton: webApp?.MainButton,
    backButton: webApp?.BackButton,
    haptic: webApp?.HapticFeedback,
    openLink: (url: string) => webApp?.openLink(url),
    openTelegramLink: (url: string) => webApp?.openTelegramLink(url),
    openInvoice: (url: string, callback?: (status: string) => void) => 
      webApp?.openInvoice(url, callback),
    showPopup: (params: any, callback?: (buttonId: string) => void) => 
      webApp?.showPopup(params, callback),
    showAlert: (message: string, callback?: () => void) => 
      webApp?.showAlert(message, callback),
    showConfirm: (message: string, callback?: (confirmed: boolean) => void) => 
      webApp?.showConfirm(message, callback),
    close: () => webApp?.close(),
    shareUrl: (url: string) => {
      // Use Telegram's share functionality
      const shareLink = `https://t.me/share/url?url=${encodeURIComponent(url)}`;
      webApp?.openTelegramLink(shareLink);
    },
  };
}
